import { getReasonCode, isReasonCode } from '@paguz/contracts';
import { Badge, Button, cn } from '@paguz/ui';
import {
  CheckIcon,
  ChevronDownIcon,
  CircleHelpIcon,
  Loader2Icon,
  RotateCcwIcon,
  Undo2Icon,
  XIcon,
} from 'lucide-react';
import { useState } from 'react';
import type { ExternalCall, ExternalSystem, SagaStepDetail } from '../../api/admin-schemas.ts';
import { JsonBlock } from '../../components/page.tsx';
import { formatDuration, formatTimeMs } from '../../lib/format.ts';
import { SAGA_STEP, STEP_STATUS, SYSTEM_LABEL } from '../../lib/labels.ts';

/** Esperas mayores a esto se comprimen en la cascada (p. ej. reintentos a los 15 min). */
const GAP_CAP_MS = 3000;

const SYSTEM_STYLE: Readonly<Record<ExternalSystem, { chip: string; bar: string }>> = {
  PAGUZ: { chip: 'bg-highlight-soft text-highlight-soft-foreground', bar: 'bg-violet-500' },
  CREDIT_CORE: { chip: 'bg-navy-50 text-navy-800', bar: 'bg-navy-700' },
  DEPOSIT_CORE: { chip: 'bg-slate-100 text-slate-700', bar: 'bg-slate-500' },
  BREB: { chip: 'bg-success-soft text-success-soft-foreground', bar: 'bg-success' },
  NOTIFICATIONS: { chip: 'bg-muted text-muted-foreground', bar: 'bg-slate-400' },
};

interface Segment {
  offset: number;
  width: number;
}

/** Cascada: posición y ancho relativos de cada intento, con esperas largas comprimidas. */
export function waterfall(saga: readonly SagaStepDetail[], nowMs: number) {
  let cursor = 0;
  let prevEnd: number | null = null;
  let compressed = false;
  const raw = saga.map((step) => {
    const start = new Date(step.startedAt).getTime();
    const end = step.finishedAt ? new Date(step.finishedAt).getTime() : nowMs;
    if (prevEnd !== null) {
      const gap = Math.max(0, start - prevEnd);
      if (gap > GAP_CAP_MS) compressed = true;
      cursor += Math.min(gap, GAP_CAP_MS);
    }
    const offset = cursor;
    const duration = Math.max(0, end - start);
    cursor += duration;
    prevEnd = end;
    return { offset, duration };
  });
  const total = Math.max(cursor, 1);
  const segments: Segment[] = raw.map(({ offset, duration }) => ({
    offset: (offset / total) * 100,
    width: Math.max((duration / total) * 100, 0.8),
  }));
  const first = saga[0];
  const last = saga.at(-1);
  const elapsedMs =
    first && last
      ? (last.finishedAt ? new Date(last.finishedAt).getTime() : nowMs) -
        new Date(first.startedAt).getTime()
      : 0;
  return { segments, compressed, elapsedMs };
}

function StatusIcon({ step }: { step: SagaStepDetail }) {
  const base =
    'relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full ring-4 ring-card';
  switch (step.status) {
    case 'SUCCEEDED':
      return (
        <span className={cn(base, 'bg-success text-success-foreground')}>
          <CheckIcon className="size-4" strokeWidth={3} />
        </span>
      );
    case 'FAILED':
      return (
        <span className={cn(base, 'bg-destructive text-destructive-foreground')}>
          <XIcon className="size-4" strokeWidth={3} />
        </span>
      );
    case 'UNKNOWN':
      return (
        <span className={cn(base, 'bg-warning text-warning-foreground')}>
          <CircleHelpIcon className="size-4" strokeWidth={2.5} />
        </span>
      );
    case 'COMPENSATED':
      return (
        <span className={cn(base, 'bg-slate-500 text-white')}>
          <Undo2Icon className="size-4" strokeWidth={2.5} />
        </span>
      );
    case 'STARTED':
      return (
        <span className={cn(base, 'bg-highlight text-highlight-foreground')}>
          <Loader2Icon className="size-4 animate-spin" />
        </span>
      );
  }
}

function barTone(step: SagaStepDetail): string {
  if (step.status === 'FAILED') return 'bg-destructive';
  if (step.status === 'UNKNOWN') return 'bg-warning';
  if (step.status === 'COMPENSATED')
    return 'bg-[repeating-linear-gradient(135deg,var(--color-slate-500)_0_4px,var(--color-slate-300)_4px_8px)]';
  return SYSTEM_STYLE[step.system].bar;
}

export interface SagaTimelineProps {
  saga: readonly SagaStepDetail[];
  externalCalls: readonly ExternalCall[];
  /** Pasos con "Reintentar paso" disponible. */
  retryableStepIds?: ReadonlySet<string>;
  onRetry?: (step: SagaStepDetail) => void;
  nowMs?: number;
}

export function SagaTimeline({
  saga,
  externalCalls,
  retryableStepIds,
  onRetry,
  nowMs,
}: SagaTimelineProps) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [mountedAt] = useState(() => Date.now());
  const { segments, compressed, elapsedMs } = waterfall(saga, nowMs ?? mountedAt);
  const callsById = new Map(externalCalls.map((c) => [c.id, c]));
  const systems = [...new Set(saga.map((s) => s.system))];

  if (saga.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border p-6 text-center text-muted-foreground">
        La saga no arrancó: la intención no se confirmó con SCA.
      </p>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm">
        <p className="text-muted-foreground">
          <span className="font-semibold text-foreground">{saga.length} intentos</span> en{' '}
          <span className="font-semibold text-foreground">{formatDuration(elapsedMs)}</span>
          {compressed && ' · esperas largas comprimidas en la cascada'}
        </p>
        <ul className="flex flex-wrap items-center gap-2" aria-label="Sistemas">
          {systems.map((system) => (
            <li key={system} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={cn('size-2.5 rounded-full', SYSTEM_STYLE[system].bar)} />
              {SYSTEM_LABEL[system]}
            </li>
          ))}
        </ul>
      </div>

      <ol className="relative">
        {saga.map((step, index) => {
          const segment = segments[index] ?? { offset: 0, width: 1 };
          const isOpen = open.has(step.id);
          const calls = step.externalCallIds
            .map((id) => callsById.get(id))
            .filter((c): c is ExternalCall => c !== undefined);
          const reason =
            step.reasonCode && isReasonCode(step.reasonCode)
              ? getReasonCode(step.reasonCode)
              : null;
          const isLast = index === saga.length - 1;
          const firstCompensation = step.compensation && !saga[index - 1]?.compensation;
          return (
            <li key={step.id} className="relative">
              {firstCompensation && (
                <div className="mb-2 ml-11 flex items-center gap-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  <Undo2Icon className="size-3.5" /> Compensación
                </div>
              )}
              <div className="flex gap-3">
                <div className="relative flex flex-col items-center">
                  <StatusIcon step={step} />
                  {!isLast && <span aria-hidden="true" className="w-0.5 flex-1 bg-border" />}
                </div>
                <div className={cn('min-w-0 flex-1', isLast ? 'pb-1' : 'pb-5')}>
                  <div className="grid grid-cols-1 gap-x-6 gap-y-2 xl:grid-cols-[minmax(0,1fr)_minmax(14rem,22rem)]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-[0.9375rem] font-semibold text-foreground">
                          {SAGA_STEP[step.step].label}
                        </h3>
                        <span
                          className={cn(
                            'rounded-md px-1.5 py-0.5 text-xs font-semibold',
                            SYSTEM_STYLE[step.system].chip,
                          )}
                        >
                          {SYSTEM_LABEL[step.system]}
                        </span>
                        <Badge variant={STEP_STATUS[step.status].tone}>
                          {STEP_STATUS[step.status].label}
                        </Badge>
                        {step.attempt > 1 && (
                          <Badge variant="outline">Intento {step.attempt}</Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {SAGA_STEP[step.step].description}
                        <span className="mx-1.5">·</span>
                        <code className="font-mono text-xs">{step.adapter}</code>
                      </p>
                      {(step.externalCode ?? reason) && (
                        <div className="mt-2 flex flex-wrap items-start gap-2 text-sm">
                          {step.externalCode && (
                            <span className="rounded-md border border-border px-1.5 py-0.5 font-mono text-xs text-foreground">
                              {step.externalCode}
                            </span>
                          )}
                          {reason && step.reasonCode && (
                            <span className="min-w-0">
                              <code className="font-mono text-xs font-semibold text-foreground">
                                {step.reasonCode}
                              </code>{' '}
                              <span className="text-muted-foreground">{reason.operatorDetail}</span>
                              <span className="block text-xs text-muted-foreground">
                                Al cliente: “{reason.customerMessage}”
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col justify-center gap-1.5">
                      <div className="flex items-center justify-between text-xs text-muted-foreground tabular-nums">
                        <span>{formatTimeMs(step.startedAt)}</span>
                        <span className="font-semibold text-foreground">
                          {step.durationMs === null ? 'En curso' : formatDuration(step.durationMs)}
                        </span>
                      </div>
                      <div className="relative h-2.5 rounded-full bg-muted" aria-hidden="true">
                        <span
                          className={cn('absolute inset-y-0 rounded-full', barTone(step))}
                          style={{
                            left: `${segment.offset}%`,
                            width: `${Math.min(segment.width, 100 - segment.offset)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {calls.length > 0 && (
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => {
                          setOpen((prev) => {
                            const next = new Set(prev);
                            if (next.has(step.id)) next.delete(step.id);
                            else next.add(step.id);
                            return next;
                          });
                        }}
                        className="inline-flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 text-xs font-semibold text-primary hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
                      >
                        <ChevronDownIcon
                          className={cn('size-3.5 transition-transform', isOpen && 'rotate-180')}
                        />
                        {isOpen ? 'Ocultar' : 'Ver'} llamada externa
                      </button>
                    )}
                    {retryableStepIds?.has(step.id) && onRetry && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          onRetry(step);
                        }}
                      >
                        <RotateCcwIcon /> Reintentar paso
                      </Button>
                    )}
                  </div>

                  {isOpen && calls.map((call) => <CallDetail key={call.id} call={call} />)}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function CallDetail({ call }: { call: ExternalCall }) {
  return (
    <div className="mt-2 rounded-xl border border-border bg-muted/40 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <code className="rounded bg-background px-1.5 py-0.5 font-mono font-semibold">
          {call.method}
        </code>
        <code className="font-mono break-all">{call.endpoint}</code>
        <Badge
          variant={
            call.outcome === 'OK' ? 'success' : call.outcome === 'TIMEOUT' ? 'warning' : 'danger'
          }
        >
          {call.httpStatus ?? 'sin respuesta'}
        </Badge>
        <span className="text-muted-foreground">
          {call.operation} · {formatDuration(call.latencyMs)}
        </span>
      </div>
      <div className="grid gap-2 lg:grid-cols-2">
        <div>
          <p className="mb-1 text-[0.6875rem] font-semibold tracking-wide text-muted-foreground uppercase">
            Request (saneado)
          </p>
          <JsonBlock value={call.request} className="max-h-52" />
        </div>
        <div>
          <p className="mb-1 text-[0.6875rem] font-semibold tracking-wide text-muted-foreground uppercase">
            Response
          </p>
          <JsonBlock value={call.response} className="max-h-52" />
        </div>
      </div>
    </div>
  );
}
